<template>
    <v-card
        ref="cardRef"
        class="pa-0"
        rounded="lg"
        height="100%"
        width="auto"
    >
        <v-card-title
            v-if="!showActionButton.includes(ticket.hash)"
            :class="headerColor"
            class="py-1 px-3 cursor-pointer"
            style="height: 70px"
            @click="enableActionButton"
        >
            <div
                :class="`${fontSize}__text`"
                class="d-flex wfull"
            >
                <div class="waiter">{{ waiter }}</div>

                <div class="table">
                    {{ getTicketId($t('general.table')) }}
                </div>
            </div>

            <div
                v-if="ticket.ticket.status !== 'done'"
                :class="`${fontSize}__text`"
                class="d-flex justify-space-between wfull"
            >
                <span style="font-size: 80%">
                    {{ moment(ticket.ticket.issued_at).format('HH:mm') }}
                </span>

                <v-spacer />

                <span style="font-size: 80%">
                    {{ ticketTimer }}
                </span>
            </div>

            <span
                v-else
                :class="`${fontSize}__text`"
            >
                <span style="font-size: 80%">
                    {{ moment(ticket.ticket.issued_at).format('DD MMM, HH:mm') }}
                </span>
            </span>
        </v-card-title>

        <v-card-title
            v-else-if="showActionButton.includes(ticket.hash) && !completed"
            v-click-outside="() => cancelTicketActions(ticket.hash)"
            :class="[`${fontSize}__title`, actionButtonColor]"
            class="white--text text-uppercase d-flex justify-center text-center cursor-pointer pa-1"
            style="height: 70px"
            @click="changeTicketStatus(ticket)"
        >
            {{ ticket.ticket.status !== 'done' ? $t('kds.complete_ticket') : $t('kds.recall_ticket') }}
        </v-card-title>

        <v-card-title
            v-else
            v-click-outside="() => cancelTicketActions(ticket.hash)"
            :class="[
                `${fontSize}__title`,
                { success: ticket.ticket.status !== 'done', error: ticket.ticket.status === 'done' },
            ]"
            class="white--text text-uppercase d-flex justify-center align-center cursor-pointer pa-1"
            style="height: 70px"
        >
            {{ ticket.ticket.status !== 'done' ? $t('general.completed') : $t('kds.recalled') }}
        </v-card-title>

        <v-sheet ref="itemsContainer" class="items-container" :height="contentHeight" @scroll.passive="scrollHandler">
            <v-card
                v-for="(item, index) in ticket.ticket.items"
                :key="index"
                class="px-1"
                rounded="lg"
                :color="countColor(item.product_id)"
                flat
                @click="changeItemStatus(item)"
            >
                <div
                    ref="itemRow"
                    :class="{ 'black--text': !countColor(item.product_id), 'white--text': countColor(item.product_id) }"
                    class="item-row"
                    dense
                    no-gutters
                >
                    <div class="item-count">
                        <span
                            :class="(
                                {
                                    'black--text': !countColor(item.product_id),
                                    'white--text': countColor(item.product_id),
                                },
                                `${fontSize}__text`
                            )"
                        >
                            {{ round(item.count, 2) }}
                        </span>
                    </div>

                    <div class="item-label">
                        <div
                            :class="`${fontSize}__text`"
                            style="white-space: pre-line"
                        >
                            {{ startCase(item.name?.toLowerCase()) }}
                        </div>

                        <div
                            v-if="!countColor(item.product_id)"
                            :class="`${fontSize}__caption`"
                            class="error--text d-flex flex-column"
                        >
                            <span v-for="(comment, i) in item.comments" :key="i">
                                {{ comment }}
                            </span>
                        </div>

                        <div
                            v-else
                            :class="`${fontSize}__caption`"
                            class="white--text d-flex flex-column"
                        >
                            <span v-for="(comment, i) in item.comments" :key="i">
                                {{ comment }}
                            </span>
                        </div>
                    </div>

                    <div class="item-check">
                        <transition name="bounce">
                            <v-icon
                                v-if="item.completed_at"
                                :size="iconSize"
                                :color="countColor(item.product_id) ? 'white' : 'success'"
                            >
                                far fa-check-circle
                            </v-icon>
                        </transition>
                    </div>
                </div>
            </v-card>
        </v-sheet>

        <v-btn
            v-if="showMoreItemsButton"
            ref="actionButtonRef"
            class="show-more-btn"
            block
            text
            color="primary"
            @click="showMoreItems"
        >
            <v-icon :size="iconSize">far fa-arrow-down</v-icon>
            <div class="ml-2">{{ $tc('kds.items_left', itemsOutsideContainer, { value: itemsOutsideContainer }) }}</div>
        </v-btn>
    </v-card>
</template>

<script setup>
    import { ref, computed, watch, nextTick, onMounted, defineProps } from 'vue'

    import { useAppStore } from '@/store/useAppStore'

    import moment from 'moment'
    import gsap from 'gsap'
    import { startCase, round } from 'lodash'

    import useKitchenTickets from './useKitchenTickets'
    import User from '@/models/User'
    import Table from '@/models/Table'

    const store = useAppStore()

    const props = defineProps({
        ticket: {
            type: Object,
            required: true,
        },
        disabled: {
            type: Boolean,
            default: true,
        },
    })

    const { selectedItems, completeTicket, recallTicket, completeItem } = useKitchenTickets()

    // Refs
    const itemRow = ref(null)
    const cardRef = ref(null)
    const actionButtonRef = ref(null)
    const itemsContainer = ref(null)

    const randomRenderKey = ref(0)
    const showActionButton = ref([])
    const completed = ref(false)
    const ticketTimer = ref(null)
    const loading = ref(false)
    const itemsOutsideContainer = ref(0)
    const contentHeight = ref(0)
    const warningColor = ref(props.ticket.warningColor)

    const waiter = computed(() => User.find(props.ticket.ticket.user_id)?.name)

    const completedItems = computed(() => {
        return props.ticket.ticket.items.filter((item) => item.completed_at).length
    })

    const remainingItems = computed(() => {
        return props.ticket.ticket.items.length - completedItems.value
    })

    const headerColor = computed(() => {
        let color = 'ticket-ongoing'

        if (props.ticket.ticket.status === 'done') {
            color = 'ticket-success'
        } else if (warningColor.value === 'yellow') {
            color = 'ticket-warning'
        } else if (warningColor.value === 'red') {
            color = 'ticket-expired'
        }

        return color
    })

    const actionButtonColor = computed(() => {
        let color = 'info'

        if (props.ticket.ticket.status === 'done') {
            color = 'warning'
        }

        return color
    })

    const showMoreItemsButton = computed(() => {
        let show = false

        if (itemsOutsideContainer.value > 0) {
            show = true
        }

        return show
    })

    const allDone = computed(() => props.ticket.ticket.items.every((item) => item.completed_at))

    const scrollCompareReference = computed(() => {
        let reference = 3

        if (fontSize.value === 'small') {
            reference = 4
        }

        return reference
    })

    const fontSize = computed(() => store.state.kdsSettings.fontSize)

    const yellowTimer = computed(() => store.state.kdsSettings.yellowTimer)

    const redTimer = computed(() => store.state.kdsSettings.redTimer)

    const iconSize = computed(() => {
        let size = 'medium'

        if (fontSize.value === 'small') {
            size = 'large'
        }

        if (fontSize.value === 'medium') {
            size = 'larger'
        }

        if (fontSize.value === 'large') {
            size = 'x-large'
        }

        if (fontSize.value === 'm-small') {
            size = 'small'
        }

        if (fontSize.value === 'm-medium') {
            size = 'smaller'
        }

        return size
    })

    watch(() => cardRef.value?.$el.clientHeight, () => setContentHeight())

    const getTicketId = (name) => {
        let id = '#' + props.ticket.ticket.id

        if (props.ticket.ticket.table_id) {
            id =
                name === 'Table'
                    ? 'Tbl #' + Table.find(props.ticket.ticket.table_id)?.name
                    : name + ' #' + Table.find(props.ticket.ticket.table_id)?.name
        }

        return id
    }

    const setContentHeight = () => {
        const header = 70
        const footer = 28
        const gutters = 2

        contentHeight.value = cardRef.value?.$el.clientHeight - header - gutters - footer
    }

    const enableActionButton = () => {
        if (props.disabled) return

        showActionButton.value.push(props.ticket.hash)
    }

    const changeTicketStatus = () => {
        if (loading.value) return

        if (props.ticket.ticket.status === 'done') {
            completed.value = true

            setTimeout(() => {
                recallTicket(props.ticket)
                loading.value = false
            }, 1500)

            return
        }

        loading.value = true

        if (allDone.value) {
            completed.value = true

            setTimeout(() => {
                completeTicket(props.ticket)
                loading.value = false
            }, 1500)

            return
        }

        setAllItemsCompleted().then((timeout) => {
            setTimeout(() => {
                showActionButton.value = showActionButton.value.filter((hash) => hash !== props.ticket.id)

                completeTicket(props.ticket)
                loading.value = false
            }, timeout)
        })
    }

    const setAllItemsCompleted = () => {
        return new Promise((resolve) => {
            const items = props.ticket.ticket.items.filter((item) => !item.completed_at)

            items.forEach((item, index) => {
                setTimeout(() => {
                    completeItem(props.ticket, item, true, false)

                    if (index === scrollCompareReference.value) smoothScrollToBottom()

                    if (index === items.length - 1) {
                        completed.value = true
                        resolve(1500)
                    }
                }, (1600 / remainingItems.value) * index)
            })
        })
    }

    const cancelTicketActions = (id) => {
        if (allDone.value) return

        showActionButton.value = showActionButton.value.filter((hash) => hash !== id)
    }

    const changeItemStatus = (item) => {
        if (props.ticket.ticket.status === 'done' || props.disabled) return

        if (item.completed_at) {
            completeItem(props.ticket, item, false, true)
        } else {
            completeItem(props.ticket, item, true, true)
        }

        if (remainingItems.value === 0) {
            enableActionButton()
        }
    }

    const smoothScrollToBottom = () => {
        gsap.to(itemsContainer.value.$el, {
            duration: 3,
            scrollTop: itemsContainer.value.$el.scrollHeight,
        })
    }

    const scrollHandler = async () => {
        await nextTick()

        const container = itemsContainer.value?.$el
        const containerBottom = container ? container.getBoundingClientRect().bottom : 0

        const outside = itemRow.value?.reduce((acc, item, index) => {
            if (item.getBoundingClientRect().top >= containerBottom - 9) {
                acc.push(index)
            }
            return acc
        }, [])

        itemsOutsideContainer.value = outside.length
    }

    const showMoreItems = () => {
        smoothScrollToBottom()
    }

    const countColor = (id) => {
        let color = null

        if (props.ticket.ticket.status === 'done') return color

        const selectedItem = selectedItems.value.find((item) => item.id === id)

        if (selectedItem) {
            color = selectedItem.color
        }

        return color
    }

    onMounted(async () => {
        setInterval(() => {
            randomRenderKey.value += 1

            let duration = moment.duration(moment().diff(moment(props.ticket.ticket.issued_at)))

            let hours = Math.floor(duration.asHours())
            let minutes = Math.floor(duration.asMinutes()) % 60
            let seconds = Math.floor(duration.asSeconds()) % 60

            ticketTimer.value = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`

            const totalTime = hours * 60 + minutes

            const biggerAndEqualThanYellow = totalTime >= yellowTimer.value
            const smallerThanRed = totalTime < redTimer.value
            const biggerAndEqualThanRed = totalTime >= redTimer.value

            if (biggerAndEqualThanYellow && smallerThanRed) {
                warningColor.value = 'yellow'
            }

            if (biggerAndEqualThanRed) {
                warningColor.value = 'red'
            }
        }, 1000)

        scrollHandler()
    })
</script>

<style lang="scss" scoped>
    .ticket-ongoing {
        background-color: #e2e2e2;
    }

    .ticket-success {
        color: white;
        background-color: #429e45;
    }

    .ticket-warning {
        background-color: #ffc61b;
    }

    .ticket-expired {
        color: white;
        background-color: #e93b3b;
    }

    .waiter {
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
        flex: 1;
        margin-right: 8px;
        font-weight: 500;
    }

    .table {
        font-weight: 500;
    }

    .items-container {
        padding: 8px;
        overflow-y: auto;
        border-bottom-left-radius: 12px;
        border-bottom-right-radius: 12px;
        *::-webkit-scrollbar {
            display: none;
        }
        -ms-overflow-style: none;
        scrollbar-width: none;

        .item-row {
            margin-bottom: 1px;
            display: flex;

            .item-count {
                min-width: 27px;
                margin-right: 8px;
                text-align: center;
            }

            .item-label {
                flex: 1;
            }

            .item-check {
                display: flex;
                justify-content: center;
                padding: 0 4px;
            }
        }
    }

    .items-container::-webkit-scrollbar {
        width: 0px !important;
    }

    .theme--light.v-btn.v-btn--disabled.v-btn--has-bg {
        background-color: transparent !important;
    }

    .show-more-btn {
        background-color: #ededed;
        position: absolute;
        bottom: 0;
        justify-content: start;
        text-transform: none;
        border-top-left-radius: 0px;
        border-top-right-radius: 0px;
        border-bottom-left-radius: 12px;
        border-bottom-right-radius: 12px;
    }

    .bounce-enter-active {
        animation: bounce-in 0.5s;
    }

    .bounce-leave-active {
        animation: bounce-in 0.5s reverse;
    }

    @keyframes bounce-in {
        0% {
            transform: scale(0);
        }
        50% {
            transform: scale(1.25);
        }
        100% {
            transform: scale(1);
        }
    }
</style>
